import { getDataWithQueryString } from '../../../api/componentAPIs';

const ERROR_MESSAGES = {
  NO_GPS_DATA: 'No GPS data found',
  FETCH_BLOB_ERROR: 'Error fetching GPS data blob',
  FETCH_PARSE_ERROR: 'Error fetching or parsing GPS data'
};

const handleError = ({ snackbarKey, closeSnackbar, enqueueSnackbar, errorMessage }) => {
  closeSnackbarFunction(snackbarKey, closeSnackbar);
  enqueueSnackbar(errorMessage, { variant: 'error' });
  return { gpsData: [], dispatchPings: null };
};

const closeSnackbarFunction = (snackbarKey, closeSnackbar) => {
  if (snackbarKey && closeSnackbar) {
    closeSnackbar(snackbarKey);
  }
};

const fetchGpsPings = async ({ dispatchId, gpsData, enqueueSnackbar, closeSnackbar, snackbarKey }) => {  
  const blobUrl = gpsData?.find((gps) => gps.dispatchId === dispatchId)?.blobUrl || null;  
  if (!blobUrl) {
    closeSnackbarFunction(snackbarKey, closeSnackbar);
    enqueueSnackbar(ERROR_MESSAGES.NO_GPS_DATA, { variant: 'info' });
    return { gpsData: gpsData, dispatchPings: null };
  }  
  try {
    const blobResponse = await fetch(blobUrl);
    if (!blobResponse.ok) {
      return handleError({
        snackbarKey,
        closeSnackbar,
        enqueueSnackbar,
        errorMessage: ERROR_MESSAGES.FETCH_BLOB_ERROR
      });
    }    
    const jsonData = await blobResponse.json();
    closeSnackbarFunction(snackbarKey, closeSnackbar);
    return { gpsData: gpsData, dispatchPings: { [dispatchId]: jsonData } };
  } catch (error) {
    return handleError({
      snackbarKey,
      closeSnackbar,
      enqueueSnackbar,
      errorMessage: ERROR_MESSAGES.FETCH_BLOB_ERROR
    });
  }
};

async function fetchRouteBlob({
  terminalCode,
  dispatchDate,
  driverId,
  isLiveDispatch,
  dispatchId,
  enqueueSnackbar,
  closeSnackbar
}) {  
  const snackbarKey = enqueueSnackbar('Fetching GPS is in progress...', {
    variant: 'success',
    persist: true
  });
  const formatDate = dispatchDate ? dispatchDate.split(' ')[0] : '';
  const baseQuery = `terminalCode=${terminalCode}&dispatchDate=${formatDate}&driverId=${driverId}`;
  const queryString = isLiveDispatch ? `${baseQuery}&dispatchId=${dispatchId}` : baseQuery;
  try {
    const response = await getDataWithQueryString('GetGPSDataBlobs', queryString);    
    if (response?.status === 200 && response.data?.length > 0) {
      return fetchGpsPings({
        dispatchId,
        gpsData: response.data,
        enqueueSnackbar,
        closeSnackbar,
        snackbarKey
      });
    }    
    return handleError({
      snackbarKey,
      closeSnackbar,
      enqueueSnackbar,
      errorMessage: ERROR_MESSAGES.FETCH_PARSE_ERROR
    });
  } catch (error) {
    return handleError({
      snackbarKey,
      closeSnackbar,
      enqueueSnackbar,
      errorMessage: ERROR_MESSAGES.FETCH_PARSE_ERROR
    });
  }
};

// Optimized GPS data filter with better performance
const getRouteGpsData = ({ startDateTime, endDateTime, gpsData = [] }) => {
  // If neither start nor end datetime is provided, return empty array
  if (!startDateTime && !endDateTime) {
    return [];
  }
  let start = startDateTime ? Date.parse(startDateTime) : null;
  let end = endDateTime ? Date.parse(endDateTime) : null;
  return gpsData.filter((point) => {
    const eventTime = Date.parse(point.eventTime);
    // If both start and end times are provided, check if event is within range
    if (start !== null && end !== null) {
      return eventTime >= start && eventTime <= end;
    }
    // If only start time is provided, check if event is after start time
    else if (start !== null) {
      return eventTime >= start;
    }
    // If only end time is provided (or neither), exclude the point
    return false;
  });
};

const getGpsPingIcon = (markerData) => {
  return (
    <div>
      <div data-markername="gps-ping-icon" data-tooltip={JSON.stringify([markerData.eventTime])}>
        <svg width="8" height="8" viewBox="0 0 8 8" style={{ display: 'block' }}>
          <circle
            cx="4"
            cy="4"
            r="3"
            fill={markerData.color}
            stroke={markerData.color}
            strokeWidth="1"
          />
        </svg>
      </div>
    </div>
  );
};

const gpsPingsWithKey = (gpsPings, theme) => {
  return gpsPings.map((ping, index) => ({
    ...ping,
    key: `gps-ping-${index}`,
    color: theme.actualRouteColor
  }));
};

export { fetchRouteBlob, getRouteGpsData, getGpsPingIcon, gpsPingsWithKey, fetchGpsPings };
